import { useEffect, useMemo } from 'react';

import cn from 'classnames';
import { useLocale } from 'hooks/locale';
import { Tab, Tabs } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useSelector } from 'react-redux';

import { BillingStructure } from './components/BillingStructure/BillingStructure';

import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectCompany } from 'api/company-slice';
import { useGetPurchasesQuery } from 'api/purchases-api';
import { useGetUploadsQuery } from 'api/upload-api';

import styles from './Billing.module.scss';

export const Billing = ({ setLoading = () => {} }) => {
  const { t } = useTranslation();
  const companyChecks = useSelector(selectCompanyChecks);
  const company = useSelector(selectCompany);
  const reportsOnly = Boolean(company?.is_company_group_parent);

  const { data: purchases } = useGetPurchasesQuery(undefined, { skip: reportsOnly });
  const { data: uploads } = useGetUploadsQuery({ billing: true });

  const { dateSlashFormat } = useLocale(company?.locale);

  useEffect(() => {
    setLoading(false);
  }, []);

  return (
    <div className={cn(styles.root)}>
      <div className={cn('card--large', 'card', 'card-with-border', 'max-w-[120rem]')}>
        <div className="u-padding--large">
          <div className={cn('u-flex-box', 'u-flex-justify-between', 'u-flex-align-center')}>
            <h1 className="title-2">{t('company.tabs.billing.title')}</h1>
          </div>
          <p className="t-small">
            {reportsOnly
              ? t('company.tabs.billing.reportsOnlyDescription')
              : t('company.tabs.billing.description')}
          </p>
        </div>
        <div className="background-secondary rounded-b-[10px]">
          <Tabs defaultActiveKey={reportsOnly ? 'reports' : 'setup'} className="tabs !pl-[19px]">
            {!reportsOnly && (
              <Tab eventKey="setup" title={t('company.tabs.billing.tabs.setup')}>
                <BillingStructure />
              </Tab>
            )}
            {!reportsOnly && (
              <Tab eventKey="events" title={t('company.tabs.billing.tabs.events')}>
                <>
                  {purchases?.map(purchase => (
                    <Purchase
                      purchase={purchase}
                      dateSlashFormat={dateSlashFormat}
                      companyChecks={companyChecks}
                    />
                  ))}
                  <div className="t-tiny u-padding-y--small">
                    <Trans
                      i18nKey="company.tabs.billing.events.historyStart"
                      components={[
                        <a className="u-link dark" href="mailto:support@checkmate.tech" />,
                      ]}
                    />
                  </div>
                </>
              </Tab>
            )}
            <Tab eventKey="reports" title={t('company.tabs.billing.tabs.reports')}>
              {uploads?.map(upload => (
                <div className="u-padding-y--small t-small">
                  <Moment format={dateSlashFormat}>{upload.created_at}</Moment>
                  <a target="_blank" className="u-margin-left--small u-link dark" href={upload.url}>
                    {upload.file_name}
                  </a>
                </div>
              ))}
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

const Purchase = ({ purchase, dateSlashFormat, companyChecks }) => {
  const companyCheck = useMemo(
    () => companyChecks.find(companyCheck => companyCheck.type === purchase.check_type),
    [purchase.check_type],
  );

  const text = useMemo(() => {
    const values = {
      units: purchase.units,
      check: companyCheck ? companyCheck.check_type.title : purchase.check_type,
      user: purchase.required_by,
    };
    const i18nKey = purchase.deduction
      ? 'company.tabs.billing.events.creditsDeducted'
      : purchase.units < 0
        ? 'company.tabs.billing.events.unlimitedCredits'
        : 'company.tabs.billing.events.creditsPurchased';

    return <Trans i18nKey={i18nKey} values={values} components={[<b />]} />;
  }, [purchase]);

  return (
    <div className="u-padding-y--small t-small">
      <Moment format={dateSlashFormat}>{purchase.created_at}</Moment>
      <span className="u-margin-left--small">{text}</span>
    </div>
  );
};
